import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MuiMenuItem from '@mui/material/MenuItem';
import MuiSelect, { type SelectChangeEvent } from '@mui/material/Select';
import TablePaginationActions, {
  type TablePaginationActionsProps,
} from '@mui/material/TablePaginationActions';
import { type ReactElement } from 'react';
import { s } from '../../theme/tokens';
import { Box } from './Box';

const buildPageOptions = (pageCount: number): number[] =>
  Array.from({ length: pageCount }, (_value, index) => index);

const PAGINATION_FONT_SIZE_PX = '17px';

const buildDisplayedRowsLabel = (count: number, page: number, rowsPerPage: number): string => {
  if (count === 0) {
    return '0-0 of 0';
  }

  const from = page * rowsPerPage + 1;
  const to = count === -1 ? (page + 1) * rowsPerPage : Math.min(count, (page + 1) * rowsPerPage);
  const totalLabel = count === -1 ? `more than ${to}` : String(count);

  return `${from}-${to} of ${totalLabel}`;
};

export const TablePaginationPageActions = ({
  className,
  count,
  disabled = false,
  onPageChange,
  page,
  rowsPerPage,
  ...actionsProps
}: TablePaginationActionsProps): ReactElement => {
  const pageCount = count < 0 ? page + 1 : Math.max(1, Math.ceil(count / Math.max(1, rowsPerPage)));
  const pageOptions = buildPageOptions(pageCount);
  const pageSelectDisabled = disabled || pageCount <= 1;
  const displayedRowsLabel = buildDisplayedRowsLabel(count, page, rowsPerPage);

  const handlePageSelectChange = (event: SelectChangeEvent<number>): void => {
    const nextPage = event.target.value;
    if (!Number.isFinite(nextPage) || nextPage === page) {
      return;
    }
    onPageChange(null, nextPage);
  };

  return (
    <Box
      className={className}
      sx={{
        display: 'contents',
      }}
    >
      <Box
        className="MuiTablePagination-pageSelect"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: s('md'),
        }}
      >
        <Box component="p" className="MuiTablePagination-selectLabel">
          Page:
        </Box>
        <MuiSelect<number>
          className="MuiTablePagination-input"
          disabled={pageSelectDisabled}
          disableUnderline
          variant="standard"
          value={page}
          onChange={handlePageSelectChange}
          IconComponent={KeyboardArrowDownIcon}
          SelectDisplayProps={{
            className: 'MuiTablePagination-select',
          }}
          MenuProps={{
            slotProps: {
              paper: {
                sx: {
                  minWidth: '48px',
                  backgroundColor: (theme) => theme.app.colors.surface,
                  backgroundImage: 'none',
                  border: (theme) => `1px solid ${theme.app.colors.border}`,
                  borderRadius: (theme) => theme.app.radii.md,
                  boxShadow: 'none',
                  overflow: 'hidden',
                  '--Paper-overlay': 'none',
                  '--Paper-shadow': 'none',
                },
              },
              list: {
                role: 'listbox',
                sx: {
                  paddingTop: s('none'),
                  paddingBottom: s('none'),
                },
              },
            },
          }}
          sx={{
            minWidth: 0,
            fontFamily: (theme) => theme.typography.body.fontFamily,
            fontSize: PAGINATION_FONT_SIZE_PX,
            fontWeight: (theme) => theme.typography.body.fontWeight,
            lineHeight: (theme) => theme.typography.body.lineHeight,
            '& .MuiSelect-select': {
              paddingLeft: s('none'),
              fontFamily: (theme) => theme.typography.body.fontFamily,
              fontSize: PAGINATION_FONT_SIZE_PX,
              fontWeight: (theme) => theme.typography.body.fontWeight,
              lineHeight: (theme) => theme.typography.body.lineHeight,
            },
            '& .MuiSvgIcon-root': {
              fontSize: '20px',
            },
          }}
        >
          {pageOptions.map((pageOption) => (
            <MuiMenuItem
              key={pageOption}
              value={pageOption}
              className="MuiTablePagination-menuItem"
              sx={{
                color: (theme) => theme.app.colors.text,
                minHeight: s('4xl'),
                paddingTop: s('none'),
                paddingBottom: s('none'),
                paddingLeft: s('md'),
                paddingRight: s('md'),
                display: 'flex',
                justifyContent: 'center',
                textAlign: 'center',
                fontFamily: (theme) => theme.typography.body.fontFamily,
                fontSize: PAGINATION_FONT_SIZE_PX,
                fontWeight: (theme) => theme.typography.body.fontWeight,
                lineHeight: (theme) => theme.typography.body.lineHeight,
                backgroundColor: (theme) => `${theme.app.colors.surface} !important`,
                border: '0 !important',
                cursor: 'pointer',
                '&&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
                '&&.Mui-selected': {
                  backgroundColor: 'transparent !important',
                },
                '&[aria-selected="true"]': {
                  backgroundColor: 'transparent !important',
                },
                '&&.Mui-selected:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
                '&[aria-selected="true"]:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
                '&&.Mui-focusVisible': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
                '&&.Mui-selected.Mui-focusVisible': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
              }}
            >
              {pageOption + 1}
            </MuiMenuItem>
          ))}
        </MuiSelect>
      </Box>
      <Box className="MuiTablePagination-displayedRowsCustom">{displayedRowsLabel}</Box>
      <Box className="MuiTablePagination-navigation">
        <TablePaginationActions
          count={count}
          disabled={disabled}
          onPageChange={onPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          {...actionsProps}
        />
      </Box>
    </Box>
  );
};
